import { supabase } from '../lib/supabase';

export const sessionService = {
  async getSessions(userId, userRole) {
    const filterField = userRole === 'student' ? 'student_id' : 'institute_id';
    
    const { data, error } = await supabase?.from('counseling_sessions')?.select(`
        *,
        student:student_id(id, name, email),
        institute:institute_id(id, name, email)
      `)?.eq(filterField, userId)?.order('scheduled_at', { ascending: true });
    
    if (error) throw error;
    
    const fromCounselingSessions = (data || [])?.map(session => ({
      id: session?.id,
      studentId: session?.student_id,
      instituteId: session?.institute_id,
      sessionType: session?.session_type,
      scheduledAt: session?.scheduled_at,
      durationMinutes: session?.duration_minutes,
      status: session?.status,
      meetingUrl: session?.meeting_url,
      recordingUrl: session?.recording_url,
      createdAt: session?.created_at,
      student: session?.student,
      institute: session?.institute,
      source: 'counseling_sessions'
    }));

    // Also fetch from counselor_bookings (student-booked sessions via virtual booth)
    // Join institute_profiles to get the proper institute_name
    const { data: bookings, error: bookingsError } = await supabase?.from('counselor_bookings')?.select(`
        *,
        student:student_id(id, name, email),
        institute:institute_id(id, name, email, institute_profiles(institute_name))
      `)?.eq(filterField, userId)?.in('status', ['confirmed', 'pending'])?.order('booking_date', { ascending: true })?.order('booking_time', { ascending: true });

    if (bookingsError) {
      console.error('Error fetching counselor_bookings:', bookingsError);
    }

    const fromBookings = (bookings || [])?.map(booking => {
      // Resolve institute name: prefer institute_profiles.institute_name, fallback to users.name
      const instituteProfileName = booking?.institute?.institute_profiles?.[0]?.institute_name;
      const instituteName = instituteProfileName || booking?.institute?.name;
      return {
        id: booking?.id,
        studentId: booking?.student_id,
        instituteId: booking?.institute_id,
        sessionType: 'one_on_one',
        scheduledAt: `${booking?.booking_date}T${booking?.booking_time || '00:00'}`,
        durationMinutes: booking?.duration_minutes || 30,
        status: booking?.status === 'confirmed' ? 'scheduled' : booking?.status,
        meetingUrl: booking?.meeting_link || null,
        createdAt: booking?.created_at,
        student: booking?.student,
        institute: { ...booking?.institute, name: instituteName },
        source: 'counselor_bookings'
      };
    });

    const merged = [...fromCounselingSessions, ...fromBookings];
    merged?.sort((a, b) => new Date(a?.scheduledAt)?.getTime() - new Date(b?.scheduledAt)?.getTime());
    return merged;
  },

  async createSession(sessionData) {
    const { data, error } = await supabase?.from('counseling_sessions')?.insert({
        student_id: sessionData?.studentId,
        institute_id: sessionData?.instituteId,
        session_type: sessionData?.sessionType,
        scheduled_at: sessionData?.scheduledAt,
        duration_minutes: sessionData?.durationMinutes || 30,
        status: 'scheduled',
        meeting_url: sessionData?.meetingUrl
      })?.select()?.single();
    
    if (error) throw error;
    
    // Create notification for student about new session
    try {
      const { notificationService } = await import('./notificationService');
      
      // Get institute details
      const { data: institute } = await supabase
        ?.from('users')
        ?.select('name')
        ?.eq('id', sessionData?.instituteId)
        ?.single();
      
      await notificationService?.notifyLiveSession({
        userId: sessionData?.studentId,
        sessionId: data?.id,
        instituteName: institute?.name || 'Institute',
        sessionType: sessionData?.sessionType,
        scheduledAt: sessionData?.scheduledAt
      });
    } catch (notifError) {
      console.error('Error creating session notification:', notifError);
    }
    
    return data;
  },

  async updateSessionStatus(sessionId, status) {
    // Try counseling_sessions first
    const { data: csData, error: csError } = await supabase
      ?.from('counseling_sessions')
      ?.update({ status })
      ?.eq('id', sessionId)
      ?.select()
      ?.single();

    if (!csError && csData) {
      // Notify when session goes live
      if (status === 'ongoing') {
        try {
          const { notificationService } = await import('./notificationService');
          const { data: session } = await supabase
            ?.from('counseling_sessions')
            ?.select('student_id, institute_id')
            ?.eq('id', sessionId)
            ?.single();
          const { data: institute } = await supabase
            ?.from('users')
            ?.select('name')
            ?.eq('id', session?.institute_id)
            ?.single();
          await notificationService?.createInAppNotification({
            userId: session?.student_id,
            title: 'Session is Live Now!',
            message: `Your session with ${institute?.name || 'Institute'} has started. Join now!`,
            category: 'session',
            metadata: { sessionId },
            actionUrl: `/video-counseling?session=${sessionId}`,
            priority: 'urgent'
          });
        } catch (notifError) {
          console.error('Error creating live session notification:', notifError);
        }
      }
      return csData;
    }

    // If not found in counseling_sessions, try counselor_bookings
    // Map status values: ongoing -> confirmed, completed -> completed, cancelled -> cancelled
    const bookingStatus = status === 'ongoing' ? 'confirmed' : status;
    const { data: cbData, error: cbError } = await supabase
      ?.from('counselor_bookings')
      ?.update({ status: bookingStatus })
      ?.eq('id', sessionId)
      ?.select()
      ?.single();

    if (cbError) {
      console.error('Error updating session status in counselor_bookings:', cbError);
      // Don't throw — session may not exist in either table
      return null;
    }

    return cbData;
  },

  async getUpcomingSessions(userId, userRole) {
    const filterField = userRole === 'student' ? 'student_id' : 'institute_id';
    const now = new Date()?.toISOString();
    const today = new Date()?.toISOString()?.split('T')?.[0];

    // Fetch from counseling_sessions (institute-created sessions)
    const { data: counselingSessions, error: csError } = await supabase?.from('counseling_sessions')?.select(`
        *,
        student:student_id(id, name, email),
        institute:institute_id(id, name, email)
      `)?.eq(filterField, userId)?.gte('scheduled_at', now)?.in('status', ['scheduled', 'ongoing'])?.order('scheduled_at', { ascending: true })?.limit(10);

    if (csError) console.error('Error fetching counseling_sessions:', csError);

    // Fetch from counselor_bookings (student-booked sessions via virtual booth)
    const { data: counselorBookings, error: cbError } = await supabase?.from('counselor_bookings')?.select(`
        *,
        institute:institute_id(id, name, email, institute_profiles(institute_name)),
        student:student_id(id, name, email)
      `)?.eq(filterField, userId)?.gte('booking_date', today)?.in('status', ['confirmed', 'pending'])?.order('booking_date', { ascending: true })?.order('booking_time', { ascending: true })?.limit(10);

    if (cbError) console.error('Error fetching counselor_bookings:', cbError);

    const fromCounselingSessions = (counselingSessions || [])?.map(session => ({
      id: session?.id,
      studentId: session?.student_id,
      instituteId: session?.institute_id,
      sessionType: session?.session_type,
      scheduledAt: session?.scheduled_at,
      durationMinutes: session?.duration_minutes,
      status: session?.status,
      meetingUrl: session?.meeting_url,
      student: session?.student,
      institute: session?.institute,
      source: 'counseling_sessions'
    }));

    const fromCounselorBookings = (counselorBookings || [])?.map(booking => {
      const instituteProfileName = booking?.institute?.institute_profiles?.[0]?.institute_name;
      const instituteName = instituteProfileName || booking?.institute?.name;
      return {
        id: booking?.id,
        studentId: booking?.student_id,
        instituteId: booking?.institute_id,
        sessionType: 'one_on_one',
        bookingDate: booking?.booking_date,
        bookingTime: booking?.booking_time,
        scheduledAt: `${booking?.booking_date}T${booking?.booking_time || '00:00'}`,
        durationMinutes: booking?.duration_minutes,
        status: booking?.status,
        meetingUrl: booking?.meeting_link || null,
        student: booking?.student,
        institute: { ...booking?.institute, name: instituteName },
        source: 'counselor_bookings'
      };
    });

    // Merge and sort: counseling_sessions by scheduledAt, counselor_bookings by bookingDate+bookingTime
    const merged = [...fromCounselingSessions, ...fromCounselorBookings];
    merged?.sort((a, b) => {
      const aTime = a?.scheduledAt
        ? new Date(a?.scheduledAt)?.getTime()
        : new Date(`${a?.bookingDate}T${a?.bookingTime || '00:00'}`)?.getTime();
      const bTime = b?.scheduledAt
        ? new Date(b?.scheduledAt)?.getTime()
        : new Date(`${b?.bookingDate}T${b?.bookingTime || '00:00'}`)?.getTime();
      return aTime - bTime;
    });

    return merged?.slice(0, 10);
  }
};